import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function AddFollowUpDialog({ open, onOpenChange, decisionId, onAdded }) {
  const { getAuthHeaders, API } = useAuth();
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(addDays(new Date(), 7)); // Default to 1 week from now
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/decisions/${decisionId}/followups`,
        {
          description: description.trim(),
          due_date: format(dueDate, 'yyyy-MM-dd')
        },
        { headers: getAuthHeaders() }
      );
      
      onAdded(response.data);
      resetForm();
      onOpenChange(false);
      toast.success('Follow-up added');
    } catch (error) {
      console.error('Error adding follow-up:', error);
      toast.error('Failed to add follow-up');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setDueDate(addDays(new Date(), 7));
  };

  const handleOpenChange = (open) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-followup-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">Add Follow-up Action</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="followup-description" className="text-sm tracking-wide text-muted-foreground uppercase">
              Action
            </Label>
            <Input
              id="followup-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What needs to be done?"
              className="input-underline text-base py-3"
              data-testid="followup-description-input"
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label className="text-sm tracking-wide text-muted-foreground uppercase">
              Due Date
            </Label>
            <Popover modal={true}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                  data-testid="followup-date-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={(d) => d && setDueDate(d)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              data-testid="cancel-add-followup"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm"
              data-testid="submit-add-followup"
            >
              {loading ? 'Adding...' : 'Add Follow-up'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
